import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { QuickSearchStore } from '@lassi/webapp-shared';

@Component({
  selector: 'lassi-quick-search-categories',
  imports: [
    MatChipsModule,
    MatMenuModule,
    MatCheckboxModule,
    MatIconModule,
    MatDivider,
  ],
  template: `
    <mat-chip
      #searchTypeMenuTrigger="matMenuTrigger"
      [matMenuTriggerFor]="searchTypeMenu"
      data-testid="search-type-menu-trigger"
      tabindex="0"
      (keydown.space)="searchTypeMenuTrigger.openMenu()"
      (menuOpened)="firstCheckbox.focus()"
    >
      {{ searchTypeName() }}
      <mat-chip-trailing-icon>
        <mat-icon>arrow_drop_down</mat-icon>
      </mat-chip-trailing-icon>
    </mat-chip>
    <mat-menu #searchTypeMenu="matMenu">
      <div
        #searchTypeMenuContainer
        class="menu-container"
        (click)="$event.stopPropagation()"
        (keydown)="$event.stopPropagation()"
        (keydown.escape)="searchTypeMenuTrigger.closeMenu()"
        tabindex="0"
      >
        <mat-checkbox
          #firstCheckbox
          [checked]="allSelected()"
          [indeterminate]="!allSelected() && !noneSelected()"
          (change)="toggleAll($event.checked)"
          >All selected</mat-checkbox
        >
        <mat-divider />
        <div data-testid="search-type-menu">
          @for (option of searchTypeList; track option.name) {
            <mat-checkbox
              [attr.data-testid]="'checkbox_' + option.name"
              [checked]="checked(option.value)"
              (change)="handleSearchTypeChange(option.value, $event.checked)"
            >
              {{ option.name }}
            </mat-checkbox>
          }
        </div>
      </div>
    </mat-menu>
  `,
  styles: `
    @use 'quick-search' as qs;

    @include qs.quickSearch();
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickSearchCategoriesComponent {
  private readonly store = inject(QuickSearchStore);
  readonly searchTypes = this.store.searchTypes;
  readonly searchTypeList = this.store.searchTypeList;

  readonly searchTypeMenuTrigger = viewChild.required<MatMenuTrigger>(
    'searchTypeMenuTrigger',
  );
  readonly searchTypeMenuContainer = viewChild.required<
    ElementRef<HTMLDivElement>
  >('searchTypeMenuContainer');

  checked(option: string) {
    return this.searchTypes().find((q) => q === option);
  }

  searchTypeName = computed(() => {
    if (this.allSelected()) {
      return 'All selected';
    } else if (this.noneSelected()) {
      return 'None selected';
    }
    return this.searchTypes().length + ' selected';
  });

  allSelected = computed(
    () => this.searchTypes().length === this.searchTypeList.length,
  );
  noneSelected = computed(() => this.searchTypes().length === 0);

  handleSearchTypeChange(searchType: string, checked: boolean) {
    this.store.setSearchTypes(
      checked
        ? [...this.searchTypes(), searchType]
        : this.searchTypes().filter((t) => t !== searchType),
    );
  }

  toggleAll(checked: boolean) {
    this.store.setSearchTypes(
      checked ? this.searchTypeList.map((type) => type.value) : [],
    );
  }
}
